/**
 * GraphQL Operations for PSP Assignments
 * Queries and mutations for managing PSP-to-client assignments
 */

import { gql } from '@apollo/client';

// =============================================================================
// Fragments
// =============================================================================

export const PSP_ASSIGNMENT_FIELDS = gql`
  fragment PspAssignmentFields on PspAssignment {
    id
    pspId
    pspName
    orgId
    orgName
    status
    assignedAt
    terminatedAt
    notes
    grantedBy
    createdAt
    updatedAt
  }
`;

// =============================================================================
// Queries
// =============================================================================

/**
 * Get PSP assignments for a client organization (CFI perspective)
 */
export const GET_PSP_ASSIGNMENTS_FOR_ORG = gql`
  query GetPspAssignmentsForOrg($orgId: ID!) {
    pspAssignmentsForOrg(orgId: $orgId) {
      ...PspAssignmentFields
    }
  }
  ${PSP_ASSIGNMENT_FIELDS}
`;

/**
 * Get client assignments for a PSP organization (PSP perspective)
 */
export const GET_PSP_ASSIGNMENTS_FOR_PSP = gql`
  query GetPspAssignmentsForPsp($pspOrgId: ID!) {
    pspAssignmentsForPsp(pspOrgId: $pspOrgId) {
      ...PspAssignmentFields
    }
  }
  ${PSP_ASSIGNMENT_FIELDS}
`;

/**
 * Get available PSPs that can be assigned to a client org
 */
export const GET_AVAILABLE_PSPS_FOR_ORG = gql`
  query GetAvailablePspsForOrg($orgId: ID!) {
    availablePspsForOrg(orgId: $orgId) {
      id
      name
    }
  }
`;

/**
 * Get available clients that can be assigned to a PSP
 */
export const GET_AVAILABLE_CLIENTS_FOR_PSP = gql`
  query GetAvailableClientsForPsp($pspOrgId: ID!) {
    availableClientsForPsp(pspOrgId: $pspOrgId) {
      id
      name
    }
  }
`;

// =============================================================================
// Mutations
// =============================================================================

/**
 * Assign a PSP to a client organization
 */
export const ASSIGN_PSP_TO_CLIENT = gql`
  mutation AssignPspToClient($pspId: ID!, $orgId: ID!, $notes: String) {
    assignPspToClient(pspId: $pspId, orgId: $orgId, notes: $notes) {
      ...PspAssignmentFields
    }
  }
  ${PSP_ASSIGNMENT_FIELDS}
`;

/**
 * Terminate a PSP-client assignment
 */
export const TERMINATE_PSP_ASSIGNMENT = gql`
  mutation TerminatePspAssignment($assignmentId: ID!) {
    terminatePspAssignment(assignmentId: $assignmentId) {
      ...PspAssignmentFields
    }
  }
  ${PSP_ASSIGNMENT_FIELDS}
`;

// =============================================================================
// TypeScript Types
// =============================================================================

export type PspAssignmentStatus = 'ACTIVE' | 'SUSPENDED' | 'TERMINATED';

export interface PspAssignment {
  id: string;
  pspId: string;
  pspName: string;
  orgId: string;
  orgName: string;
  status: PspAssignmentStatus;
  assignedAt: string;
  terminatedAt: string | null;
  notes: string | null;
  grantedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface AvailablePsp {
  id: string;
  name: string;
}

export interface AvailableClient {
  id: string;
  name: string;
}

// Query response types
export interface GetPspAssignmentsForOrgData {
  pspAssignmentsForOrg: PspAssignment[];
}

export interface GetPspAssignmentsForOrgVars {
  orgId: string;
}

export interface GetPspAssignmentsForPspData {
  pspAssignmentsForPsp: PspAssignment[];
}

export interface GetPspAssignmentsForPspVars {
  pspOrgId: string;
}

export interface GetAvailablePspsForOrgData {
  availablePspsForOrg: AvailablePsp[];
}

export interface GetAvailablePspsForOrgVars {
  orgId: string;
}

export interface GetAvailableClientsForPspData {
  availableClientsForPsp: AvailableClient[];
}

export interface GetAvailableClientsForPspVars {
  pspOrgId: string;
}

// Mutation response types
export interface AssignPspToClientData {
  assignPspToClient: PspAssignment;
}

export interface AssignPspToClientVars {
  pspId: string;
  orgId: string;
  notes?: string;
}

export interface TerminatePspAssignmentData {
  terminatePspAssignment: PspAssignment;
}

export interface TerminatePspAssignmentVars {
  assignmentId: string;
}
